import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Spinner, SEO } from '../../../components/common';
import { tourService } from '../../../api/services';
import { useMoney } from '../../../hooks/useMoney';

export default function Tour() {
  const { t } = useTranslation();
  const money = useMoney();
  const [tourData, setTourData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    tourService.getList().then((data) => {
      setTourData(data.map((tt) => ({
        id: tt.id,
        slug: tt.slug,
        tag: t('tour.tag'),
        title: tt.name,
        description: tt.description || '', // PPTX 23p: 카드에 상품 설명 노출(어드민 '설명' 필드 그대로)
        price: tt.price || 0,
        thumbnail: tt.images?.length
          ? [...tt.images].sort((a, b) => a.sort_order - b.sort_order)[0].image_url
          : null,
      })));
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [t]);

  // BugHerd #214: 투어명은 BE 가 Translation 테이블에 캐시한 번역명(tt.name)을 단일 출처로 사용.
  // FE DeepL 이중번역 제거 — 화면마다 다르게 표시되던 문제 해결.

  return (
    <div className="min-h-screen bg-white">
      <SEO serviceType="Private Tour" />
      {/* Hero Section */}
      <div className="relative max-w-container mx-auto px-5 pt-4 sm:pt-6">
        {/* Gradient Box - not full width, rounded. #237: 키포인트를 박스 '안'에 배치(흰 윤곽선 아이콘). */}
        <div
          style={{ background: 'linear-gradient(to right, #0D1B2A, #0F386E)' }}
          className="rounded-2xl px-5 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 text-center"
        >
          {/* Private Tour badge - white text, semi-transparent bg */}
          <div className="inline-flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1 mb-2">
            <Icon name="place" size={15} className="text-white" />
            <span className="text-xs sm:text-sm font-bold text-white">Private Tour</span>
          </div>
          {/* SEO: 단일 h1에 키워드(title_2 '프라이빗 투어') 포함 — 색만 span 분리해 외형 유지. (SEO sr-only renderH1 중복 제거) */}
          <h1 className="text-lg sm:text-xl lg:text-2xl font-extrabold leading-tight">
            <span className="text-white">{t('tour.hero_title_1')}</span><br/>
            <span className="text-orange-500">{t('tour.hero_title_2')}</span>
          </h1>
          {/* #237: 검색창 제거 → 키포인트 4개(안전/프라이빗/단독/가이드). 네이비 박스 안, 흰 윤곽선 아이콘 + 옅은 구분선. */}
          <div className="mt-5 sm:mt-6 pt-4 border-t border-white/15 flex items-stretch justify-around">
            {[
              { icon: 'kp-safe', label: t('tour.keypoint_1') },
              { icon: 'kp-private', label: t('tour.keypoint_2') },
              { icon: 'kp-exclusive', label: t('tour.keypoint_3') },
              { icon: 'kp-guide', label: t('tour.keypoint_4') },
            ].map((kp, i) => (
              <div key={i} className={`flex-1 flex flex-col items-center justify-start gap-1.5 text-center px-0.5 ${i > 0 ? 'border-l border-white/10' : ''}`}>
                <Icon name={kp.icon} size={24} className="text-white" />
                <span className="text-2xs sm:text-2sm font-medium text-white/90 break-keep leading-tight">{kp.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <section className="max-w-content mx-auto px-2.5 sm:px-5 pt-5 sm:pt-8 pb-5 sm:pb-8">
        {loading && <div className="flex justify-center py-8"><Spinner /></div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2.5 gap-y-5 sm:gap-5">
          {tourData.map((tour) => {
            const displayTitle = tour.title;
            return (
            <Link
              key={tour.id}
              to={`/tour/${tour.slug || tour.id}`}
              className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-card hover:shadow-lg transition"
            >
              {/* Image */}
              <div className="w-full aspect-[4/3] bg-mono-100 flex items-center justify-center overflow-hidden">
                {tour.thumbnail
                  ? <img src={tour.thumbnail} alt={displayTitle} loading="lazy" className="w-full h-full object-cover" />
                  : <span className="text-sm text-mono-400">{t('tour.product_image')}</span>
                }
              </div>

              {/* Card Body — flex column 으로 title 영역이 grow, 하단 row(가격+버튼) 는 mt-auto 로 카드 하단 고정.
                  같은 row 의 카드 높이는 grid 가 stretch 정렬하므로 제목/설명 줄 수 차이와 무관하게 정렬됨.
                  PPTX 23p: 상품 설명 + "투어 보기 >" 버튼 추가, 가격을 가장 크게/오렌지로 강조. */}
              <div className="flex flex-col flex-1 px-2 py-2 sm:p-4 gap-1 sm:gap-1.5">
                <span className="text-2sm font-bold text-green-600">[{tour.tag}]</span>
                <h3 className="text-[15px] sm:text-lg font-bold text-mono-900 leading-snug group-hover:text-orange-500 transition line-clamp-2">
                  {displayTitle}
                </h3>
                {tour.description && (
                  <p className="text-[13px] sm:text-[15px] text-mono-700 leading-snug line-clamp-1 break-keep">{tour.description}</p>
                )}
                <div className="mt-auto pt-1.5 flex items-end justify-between gap-1.5">
                  <p className="flex items-baseline gap-0.5 min-w-0">
                    <span className="text-md sm:text-xl font-extrabold text-orange-500">{money(tour.price)}</span>
                    <span className="text-2sm sm:text-lg font-bold text-orange-500"> {t('tour.from')}</span>
                  </p>
                  <span className="shrink-0 inline-flex items-center gap-0.5 rounded-lg bg-orange-500 text-white text-[15px] sm:text-base font-bold px-2.5 py-1.5 group-hover:bg-orange-600 transition">
                    {t('tour.view_tour')}
                    <Icon name="chevron-right" size={14} className="text-white" />
                  </span>
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
